import { useEffect, useRef } from 'react'

import { Loader } from '@googlemaps/js-api-loader'
import { MarkerClusterer } from '@googlemaps/markerclusterer'

import type { ImageProps } from '../Image/Image'

import { infoWindowToString } from './infoWindowToString'
import { mapStyles } from './mapStyles'

import styles from './Map.module.css'

export interface MapProps {
  locations: {
    geoLocation: {
      lat: number
      lng: number
    }
    name: string
    image?: ImageProps
    town?: string
    city?: string
    telephone?: string
    address?: string
    url: {
      _type: string
      slug: string
    }
  }[]
  clickThroughToUrl?: boolean
  height?: number
}

export default function Map({
  locations,
  clickThroughToUrl = false,
  height = 400,
}: MapProps) {
  if (!process.env.NEXT_PUBLIC_MAPS_API_KEY) return

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const mapRef = useRef<HTMLDivElement>()

  const loader = new Loader({
    apiKey: process.env.NEXT_PUBLIC_MAPS_API_KEY
      ? process.env.NEXT_PUBLIC_MAPS_API_KEY
      : '',
    version: 'weekly',
  })

  const mapOptions = {
    center: {
      lat: 0,
      lng: 0,
    },
    zoom: 4,
    styles: mapStyles,
    streetViewControl: false,
    mapTypeControl: false,
    controlSize: 30,
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    let openInfoWindow: any = null

    if (mapRef?.current) {
      loader
        .importLibrary('maps')
        .then(() => {
          // Once importLibrary resolves, google.maps is available globally
          const {
            Map,
            InfoWindow,
            Marker,
            LatLngBounds,
            SymbolPath,
            Point,
            event,
          } = (window as any).google.maps
          const svgMarker = {
            path: 'M 20 10 A 10 10 0 1 0 0 10 A 10 10 0 1 0 20 10',
            fillColor: 'black',
            fillOpacity: 1,
            strokeWeight: 0,
            rotation: 0,
            scale: 2,
            anchor: new Point(10, 10),
            labelOrigin: new Point(10, 10),
          }

          const renderer = {
            render: ({
              count,
              position,
            }: {
              count: number
              position: any // LatLng | LatLngLiteral
            }) =>
              new Marker({
                label: {
                  text: String(count),
                  color: '#ffffff',
                },
                icon: svgMarker,
                position,
                // adjust zIndex to be above other markers
                zIndex: Number(Marker.MAX_ZINDEX) + count,
              }),
          }
          const map = new Map(mapRef.current as HTMLDivElement, mapOptions)

          event.addListenerOnce(map, 'bounds_changed', function (this: any) {
            this.setZoom(Math.min(this.getZoom(), 15))
          })

          const bounds = new LatLngBounds()
          const markers = locations.map(location => {
            const mapMarker = new Marker({
              position: location.geoLocation,
              map,
              title: location.name,
              icon: {
                path: SymbolPath.CIRCLE,
                scale: 7,
              },
            })

            bounds.extend(mapMarker.getPosition() as any)

            const infoWindow = new InfoWindow({
              content: infoWindowToString(
                location.name,
                location?.town,
                location?.city,
                location?.telephone,
                location?.image,
                location?.url
              ),
              maxWidth: 186,
            })

            const infoWindowAction = () => {
              if (openInfoWindow) {
                openInfoWindow.close()
              }
              infoWindow.open(map, mapMarker)
              openInfoWindow = infoWindow
            }

            mapMarker.addListener('click', infoWindowAction)
            mapMarker.addListener('mouseover', infoWindowAction)
            map.fitBounds(bounds)

            return mapMarker
          })

          new MarkerClusterer({ markers, map, renderer })
        })
        .catch(e => {
          // do something
          console.log('There was an error generating map', e)
        })
    }
  }, [mapRef, locations])

  return (
    <div
      ref={mapRef as any}
      style={{ width: '100%', height: `${height}px` }}
      className={styles.map}
    ></div>
  )
}
